import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
  Badge,
  Button,
  Drawer,
  Input,
  Select,
  Table,
  message,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Customer365LockPage from "./Customer365LockPage";
import { hasCustomer365Access } from "util/productLockConfig";

import ProfileDetailView from "./ProfileDetailView";
import { listProfiles } from "./service";
import styles from "./style.module.scss";
import { intentLabelToStackedBarRgba } from "constants/dashboardIntentColors";
import {
  computeMixedInnerTooltipBreakdown,
  resolveMixedInnerTooltipSource,
} from "util/mixedInnerTooltip";
import { translateIntentLabel } from "util/analysisLabelI18n";
import { formatAppDateTime } from "util/formatAppDate";

const PAGE_SIZE = 10;
const DRAWER_WIDTH = 680;

function profileRefFromRecord(record) {
  return record.gpuuid || String(record.id);
}

function looksLikeEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
}

function sentimentLabelClass(key) {
  if (key === "positive") return styles.sentGreen;
  if (key === "negative") return styles.sentRed;
  if (key === "mixed") return styles.sentMixed;
  return styles.sentYellow;
}

const SENTIMENT_ORDER = ["positive", "neutral", "negative", "mixed"];

function AggregatedSentimentCell({ agg, t }) {
  if (!agg?.total) {
    return "—";
  }

  const mixedSource = resolveMixedInnerTooltipSource(agg.mixed_inner, {
    entriesKey: "sentiments",
  });
  const mixedInnerBreakdown = computeMixedInnerTooltipBreakdown(
    mixedSource.mixedReviewCount,
    mixedSource.entries,
    {
      labelKey: "sentiment",
      excludeLabels: ["mixed"],
    },
  );
  const showMixedInner =
    (agg.counts?.mixed || 0) > 0 && mixedInnerBreakdown.length > 0;

  return (
    <div className={styles.sentimentAgg}>
      <div
        className={styles.sentimentAggBar}
        aria-label={t("customer_365.sentiment_breakdown", "Sentiment breakdown")}
      >
        {SENTIMENT_ORDER.map((key) => {
          const pct = agg.percentages?.[key] || 0;
          if (!pct) return null;
          return (
            <span
              key={key}
              className={styles[`sentimentBar${key.charAt(0).toUpperCase()}${key.slice(1)}`]}
              style={{ width: `${pct}%` }}
            />
          );
        })}
      </div>
      <div className={styles.sentimentAggLabels}>
        {SENTIMENT_ORDER.map((key) => {
          const count = agg.counts?.[key] || 0;
          if (!count) return null;
          return (
            <span key={key} className={sentimentLabelClass(key)}>
              {t(`customer_365.sentiment_${key}`, key.charAt(0).toUpperCase() + key.slice(1))}{" "}
              {agg.percentages?.[key] ?? 0}%
            </span>
          );
        })}
      </div>
      {showMixedInner ? (
        <div className={styles.sentimentMixedInner}>
          <div className={styles.intentMixedInnerTitle}>
            {t("customer_365.within_mixed_reviews", "within mixed reviews:")}
          </div>
          <div className={styles.intentMixedInnerRows}>
            {mixedInnerBreakdown.map((row) => (
              <span key={row.label} className={styles.intentMixedInnerRow}>
                <span
                  className={`${styles.intentMixedInnerSwatch} ${styles[`sentimentSwatch${row.label.charAt(0).toUpperCase()}${row.label.slice(1)}`]}`}
                />
                {t(
                  `customer_365.sentiment_${row.label}`,
                  row.label.charAt(0).toUpperCase() + row.label.slice(1),
                )}{" "}
                {row.percentage}%
              </span>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function AggregatedIntentCell({ agg, t }) {
  if (!agg?.total) {
    return "—";
  }

  const entries = Object.entries(agg.counts || {})
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1]);

  const mixedSource = resolveMixedInnerTooltipSource(agg.mixed_inner, {
    entriesKey: "intents",
  });
  const mixedInnerBreakdown = computeMixedInnerTooltipBreakdown(
    mixedSource.mixedReviewCount,
    mixedSource.entries,
    { labelKey: "intent", excludeLabels: ["mixed"] },
  );
  const showMixedInner =
    (agg.counts?.mixed || 0) > 0 && mixedInnerBreakdown.length > 0;

  return (
    <div className={styles.intentAgg}>
      <div
        className={styles.sentimentAggBar}
        aria-label={t("customer_365.intent_breakdown", "Intent breakdown")}
      >
        {entries.map(([label, count]) => {
          const pct = agg.percentages?.[label] || 0;
          if (!pct) return null;
          return (
            <span
              key={label}
              className={styles.intentBarSegment}
              style={{
                width: `${pct}%`,
                backgroundColor: intentLabelToStackedBarRgba(label),
              }}
              title={`${label} ${pct}%`}
            />
          );
        })}
      </div>
      <div className={styles.intentAggLabels}>
        {entries.map(([label]) => (
          <span
            key={label}
            className={styles.intentAggLabel}
            style={{ color: intentLabelToStackedBarRgba(label, 1) }}
          >
            {translateIntentLabel(label, t)} {agg.percentages?.[label] ?? 0}%
          </span>
        ))}
      </div>
      {showMixedInner ? (
        <div className={styles.intentMixedInner}>
          <div className={styles.intentMixedInnerTitle}>
            {t("customer_365.within_mixed_reviews", "within mixed reviews:")}
          </div>
          <div className={styles.intentMixedInnerRows}>
            {mixedInnerBreakdown.map((row) => (
              <span key={row.label} className={styles.intentMixedInnerRow}>
                <span
                  className={styles.intentMixedInnerSwatch}
                  style={{
                    backgroundColor: intentLabelToStackedBarRgba(row.label, 1),
                  }}
                />
                {translateIntentLabel(row.label, t)} {row.percentage}%
              </span>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

const ListPage = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const subscriptionStatus = useSelector((state) => state.userReducer);
  const theme = useSelector((state) => state.DashboardReducer.theme) || "light";
  const { profileRef: profileRefParam } = useParams();
  const profileRefFromUrl = profileRefParam
    ? decodeURIComponent(profileRefParam)
    : null;
  const initialEmail = new URLSearchParams(location.search).get("email") || "";
  const [query, setQuery] = useState(initialEmail);
  const [filter, setFilter] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const openProfile = useCallback(
    (record) => {
      const ref = profileRefFromRecord(record);
      history.push(
        `/console/customer-365/${encodeURIComponent(ref)}${location.search}`
      );
    },
    [history, location.search]
  );

  const closeProfile = useCallback(() => {
    history.push(`/console/customer-365${location.search}`);
  }, [history, location.search]);

  const load = useCallback(
    async (nextPage = page, overrideQuery) => {
      setLoading(true);
      try {
        const term = (overrideQuery != null ? overrideQuery : query).trim();
        const params = {
          page: nextPage,
          pageSize: PAGE_SIZE,
          filter: filter || undefined,
        };
        if (term) {
          if (looksLikeEmail(term)) params.email = term;
          else params.search = term;
        }
        const res = await listProfiles(params);
        if (!res || res.error || res.database_ready === false) {
          message.error(
            res?.error ||
              t("customer_365.search_error", "Could not load profiles")
          );
          setRows([]);
          setTotal(0);
          return;
        }
        setRows(res.profiles || []);
        setTotal(res.total || 0);
        setPage(res.page || nextPage);
        if ((res.profiles || []).length === 1 && looksLikeEmail(term)) {
          openProfile(res.profiles[0]);
        }
      } finally {
        setLoading(false);
      }
    },
    [filter, openProfile, page, query, t]
  );

  useEffect(() => {
    load(1, initialEmail || query);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const columns = [
    {
      title: t("customer_365.col_gpuuid", "GPUUID"),
      dataIndex: "gpuuid",
      render: (v, r) => v || `#${r.id}`,
    },
    {
      title: t("customer_365.col_last_seen", "Last activity"),
      dataIndex: "last_seen_at",
      render: (v) =>
        v ? formatAppDateTime(v, i18n.language) || "—" : "—",
    },
    {
      title: t("customer_365.col_reviews", "Reviews"),
      dataIndex: "signal_count",
      width: 90,
    },
    {
      title: t("customer_365.col_sentiment", "Sentiment"),
      dataIndex: "aggregated_sentiment",
      width: 190,
      render: (agg) => <AggregatedSentimentCell agg={agg} t={t} />,
    },
    {
      title: t("customer_365.col_intent", "Intent"),
      dataIndex: "aggregated_intent",
      width: 220,
      render: (agg) => <AggregatedIntentCell agg={agg} t={t} />,
    },
    {
      title: t("customer_365.col_recs", "Recommendations"),
      dataIndex: "active_recommendation_count",
      width: 140,
      render: (v) =>
        v > 0 ? <Badge count={v} style={{ backgroundColor: "#722ed1" }} /> : "—",
    },
  ];

  if (!hasCustomer365Access(subscriptionStatus)) {
    return <Customer365LockPage theme={theme} />;
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>
          {t("customer_365.title", "Customer 365")}
        </h1>
        <p className={styles.subtitle}>
          {t(
            "customer_365.subtitle",
            "Unified customer view across survey, social, and journey signals."
          )}
        </p>
      </div>

      <div className={styles.toolbar}>
        <Input
          allowClear
          style={{ maxWidth: 360 }}
          prefix={<SearchOutlined />}
          placeholder={t(
            "customer_365.search_placeholder",
            "Search by GPUUID (email, ticket, user ID also work)"
          )}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onPressEnter={() => load(1)}
        />
        <Select
          allowClear
          placeholder={t("customer_365.filter", "Filter")}
          style={{ width: 260 }}
          value={filter}
          onChange={setFilter}
          options={[
            {
              value: "at_risk",
              label: t(
                "customer_365.filter_at_risk",
                "At risk (low ratings, 30d)"
              ),
            },
            {
              value: "no_engagement",
              label: t(
                "customer_365.filter_quiet",
                "No recent engagement (60d)"
              ),
            },
          ]}
        />
        <Button type="primary" loading={loading} onClick={() => load(1)}>
          {t("customer_365.search", "Search")}
        </Button>
      </div>

      <div className={styles.panel}>
        <Table
          rowKey={(record) => record.gpuuid || String(record.id)}
          loading={loading}
          dataSource={rows}
          columns={columns}
          onRow={(record) => ({
            onClick: () => openProfile(record),
            style: { cursor: "pointer" },
          })}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total,
            showSizeChanger: false,
            onChange: (p) => load(p),
          }}
        />
      </div>

      <Drawer
        open={!!profileRefFromUrl}
        onClose={closeProfile}
        placement="right"
        width={DRAWER_WIDTH}
        destroyOnClose
        maskClosable
        title={profileRefFromUrl || ""}
        className={styles.detailDrawer}
        styles={{ body: { padding: 0, overflow: "auto" } }}
      >
        {profileRefFromUrl ? (
          <ProfileDetailView profileRef={profileRefFromUrl} embedded />
        ) : null}
      </Drawer>
    </div>
  );
};

export default ListPage;
